import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Film, Newspaper, Calendar, Users, BookOpen, 
  Video, MapPin, Info, Heart, MessageSquare, Briefcase,
  ChevronDown, LogIn, User, Home, Mail, Send, Loader2
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Page visibility configuration - set to true to show, false to hide
// To re-enable a page: change false to true
const PAGE_VISIBILITY = {
  directors: false,  // Set to true to re-enable Directors page
  members: true,     // Members page is now visible
};

const navItems = [
  { name: "Home", path: "/", icon: Home },
  { name: "News", path: "/news", icon: Newspaper },
  { name: "Films", path: "/films", icon: Film },
  { name: "Events", path: "/events", icon: MapPin },
  { name: "Festivals", path: "/festivals", icon: Calendar },
  { name: "Resources", path: "/resources", icon: BookOpen },
  { name: "Workshops", path: "/workshops", icon: Video },
  { name: "Forum", path: "/forum", icon: MessageSquare },
  { name: "Jobs", path: "/jobs", icon: Briefcase },
  { name: "Members", path: "/members", icon: Users, enabled: PAGE_VISIBILITY.members },
  { name: "About", path: "/about", icon: Info },
  { name: "Support", path: "/support", icon: Heart },
  { name: "Join", path: "/join", icon: Users },
  { name: "Directors", path: "/directors", icon: Users, enabled: PAGE_VISIBILITY.directors },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const location = useLocation();
  const { user, logout, loading } = useAuth();

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (user) {
        try {
          const response = await axios.get(`${API}/api/messages/unread/count`, { withCredentials: true });
          setUnreadMessages(response.data.count);
        } catch (error) {
          console.error("Error fetching unread count:", error);
        }
      }
    };
    
    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    
    setNewsletterLoading(true);
    try {
      const response = await axios.post(`${API}/api/newsletter/subscribe`, {
        email: newsletterEmail
      });
      toast.success(response.data.message);
      setNewsletterEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              data-testid="logo-link"
            >
              <img 
                src="/images/hero-image.png"
                alt="AI Film Society"
                className="h-10 w-10 object-contain"
              />
              <span className="font-syne font-bold text-xl text-white">
                AI Film Society
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.slice(0, 7).filter(item => item.enabled !== false).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`px-3 py-2 text-xs uppercase tracking-wider font-medium transition-colors ${
                    location.pathname === item.path
                      ? "text-white"
                      : "text-slate-500 hover:text-white"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* More dropdown */}
              <div className="relative group">
                <button 
                  className="px-3 py-2 text-xs uppercase tracking-wider font-medium text-slate-400 hover:text-white flex items-center gap-1"
                  data-testid="nav-more"
                >
                  More <ChevronDown className="w-3 h-3" />
                </button>
                <div className="absolute top-full right-0 mt-2 w-48 glass rounded-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {navItems.slice(7).filter(item => item.enabled !== false).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <item.icon className="w-4 h-4" />
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </nav>

            {/* Auth & Mobile Menu */}
            <div className="flex items-center gap-4">
              {!loading && (
                <>
                  {user ? (
                    <div className="hidden sm:flex items-center gap-3">
                      {["admin", "super_admin"].includes(user.role) && (
                        <Link
                          to="/admin"
                          data-testid="admin-link"
                          className="text-xs uppercase tracking-wider text-purple-light hover:text-white transition-colors"
                        >
                          Admin
                        </Link>
                      )}
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-purple/20 flex items-center justify-center">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <User className="w-4 h-4 text-purple-light" />
                          )}
                        </div>
                        <span className="text-sm text-slate-300">{user.name}</span>
                      </div>
                      <Link 
                        to="/messages"
                        data-testid="messages-link"
                        className="relative text-xs uppercase tracking-wider text-slate-400 hover:text-white transition-colors flex items-center gap-1"
                      >
                        <Mail className="w-4 h-4" />
                        {unreadMessages > 0 && (
                          <span className="absolute -top-1 -right-1 bg-purple text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                            {unreadMessages > 9 ? '9+' : unreadMessages}
                          </span>
                        )}
                      </Link>
                      <Link 
                        to="/profile"
                        data-testid="profile-link"
                        className="text-xs uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                      >
                        Profile
                      </Link>
                      <button
                        onClick={logout}
                        data-testid="logout-btn"
                        className="text-xs uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      data-testid="login-link"
                      className="hidden sm:flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-medium text-black bg-white hover:bg-slate-200 transition-colors rounded-full"
                    >
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </Link>
                  )}
                </>
              )}
              
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-slate-400 hover:text-white"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-void/95 backdrop-blur-xl pt-16 overflow-y-auto">
              <nav className="p-6 space-y-2">
                {navItems.filter(item => item.enabled !== false).map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                      className={`flex items-center gap-3 px-4 py-3 text-lg ${
                        location.pathname === item.path
                          ? "text-purple-light"
                          : "text-slate-300 hover:text-white"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                
                <div className="pt-6 border-t border-white/10">
                  {user ? (
                    <>
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className="w-10 h-10 rounded-full bg-purple/20 flex items-center justify-center">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                          ) : (
                            <User className="w-5 h-5 text-purple-light" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-sm text-slate-400">{user.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white"
                      >
                        <User className="w-5 h-5" />
                        My Profile
                      </Link>
                      <Link
                        to="/messages"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white relative"
                      >
                        <Mail className="w-5 h-5" />
                        Messages
                        {unreadMessages > 0 && (
                          <span className="ml-2 bg-purple text-white text-xs px-2 py-0.5 rounded-full">
                            {unreadMessages}
                          </span>
                        )}
                      </Link>
                      {["admin", "super_admin"].includes(user.role) && (
                        <Link
                          to="/admin"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-purple-light"
                        >
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 text-slate-400 hover:text-white"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-white bg-purple rounded-full mx-4 justify-center"
                    >
                      <LogIn className="w-5 h-5" />
                      Sign In
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Newsletter Section */}
          <div className="mb-12 pb-12 border-b border-white/5">
            <div className="max-w-xl mx-auto text-center">
              <Mail className="w-10 h-10 text-purple mx-auto mb-4" />
              <h3 className="font-syne font-bold text-2xl text-white mb-2">Stay in the Loop</h3>
              <p className="text-slate-400 mb-6">Get the latest AI filmmaking news, events, and community updates delivered to your inbox.</p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2 max-w-md mx-auto">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-void-surface border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-purple/50 transition-colors"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={newsletterLoading}
                  className="px-6 py-3 bg-purple hover:bg-purple-light text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="newsletter-subscribe-btn"
                >
                  {newsletterLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Subscribe
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div>
              <h4 className="font-syne font-bold text-white mb-4">Explore</h4>
              <div className="space-y-2">
                <Link to="/films" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Films</Link>
                <Link to="/directors" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Directors</Link>
                <Link to="/festivals" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Festivals</Link>
                <Link to="/events" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Events</Link>
                <Link to="/news" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">News</Link>
              </div>
            </div>
            <div>
              <h4 className="font-syne font-bold text-white mb-4">Learn</h4>
              <div className="space-y-2">
                <Link to="/workshops" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Workshops</Link>
                <Link to="/resources" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Resources</Link>
              </div>
            </div>
            <div>
              <h4 className="font-syne font-bold text-white mb-4">Connect</h4>
              <div className="space-y-2">
                <Link to="/forum" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Forum</Link>
                <Link to="/members" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Members</Link>
                <Link to="/resources" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Resources</Link>
              </div>
            </div>
            <div>
              <h4 className="font-syne font-bold text-white mb-4">Get Involved</h4>
              <div className="space-y-2">
                <Link to="/join" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Join</Link>
                <Link to="/support" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Support Us</Link>
                <Link to="/about" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">About Us</Link>
                <Link to="/jobs" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Jobs</Link>
                <a href="mailto:sam@aiadstudio.com" className="block text-sm text-slate-400 hover:text-white transition-colors">Contact</a>
              </div>
            </div>
            <div>
              <h4 className="font-syne font-bold text-white mb-4">Legal</h4>
              <div className="space-y-2">
                <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Terms of Service</Link>
                <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="block text-sm text-slate-400 hover:text-white transition-colors">Privacy Policy</Link>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="/images/hero-image.png"
                alt="AI Film Society"
                className="h-8 w-8 object-contain"
              />
              <span className="font-syne font-bold text-white">AI Film Society</span>
            </div>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} AI Film Society. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
